import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, ConfirmDialog, EmptyState, LoadingList, SectionHeader, cx } from '@timely/ui';
import { holds } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { Field, FormNotice } from './AuthLayout';
import { assignableRoles, whyNotEditable } from '../team/assignable';
import {
    apiMessage, changeMemberRole, inviteMember, removeMember, resendInvite,
    revokeInvite, setMemberStatus, useTeam,
    type PendingInvite, type TeamMember,
} from '../team/useTeam';
import { isEmailShaped, normaliseEmail } from '../auth/authErrors';

/**
 * The Team screen: who is in this company, who has been asked, and the controls
 * for changing both.
 *
 * Two rules run through the whole of it.
 *
 * The first is that a control which the database will refuse is disabled here
 * **with the reason showing**, not hidden and not left live. Somebody who
 * cannot promote a colleague should see why, once, rather than press a button
 * and read a constraint violation. The refusal is still the database's — this
 * only means it is not usually reached.
 *
 * The second is that no invitation token appears anywhere in this file or in
 * anything it reads. `pending_invites` returns the address, the role and the
 * dates, and the table it reads from has no client grant at all — the token's
 * digest is not reachable even by the owner of the company. The only place a
 * raw token exists is the email, once.
 */
export function Team() {
    const context = useActiveWorkspace();
    const { members, invites, roles, loading, error, reload } = useTeam(context);

    const canManage = holds(context, 'team.manage');
    const canSeeReconciliation = canManage || holds(context, 'customers.assign');
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [confirming, setConfirming] = useState<TeamMember | null>(null);

    const offerable = assignableRoles(context, roles);

    const run = async (key: string, work: () => Promise<void>, success?: string) => {
        setBusy(key);
        setNotice(null);
        try {
            await work();
            await reload();
            if (success) setNotice({ tone: 'ok', text: success });
        } catch (failed) {
            setNotice({ tone: 'error', text: failed instanceof Error ? failed.message : apiMessage(failed) });
        } finally {
            setBusy(null);
        }
    };

    return (
        <Page>
            <SectionHeader
                title="People"
                subtitle="Everybody who can sign in to this company, and what they may do."
                actions={canSeeReconciliation
                    ? <Link to="../reconciliation"><Button variant="secondary">Imported owner codes</Button></Link>
                    : undefined}
            />

            {notice && (
                <div className="mt-4">
                    <FormNotice tone={notice.tone}>{notice.text}</FormNotice>
                </div>
            )}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading the team" rows={3} />
                ) : error ? (
                    <div className="px-5 py-6">
                        <FormNotice tone="error">{error}</FormNotice>
                        <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                    </div>
                ) : members.length === 0 ? (
                    <EmptyState
                        title="Nobody here yet"
                        hint="That is unusual — a company always has at least its owner. Try reloading."
                    />
                ) : (
                    <ul className="divide-y divide-separator">
                        {members.map(member => (
                            <MemberRow
                                key={member.memberId}
                                member={member}
                                offerable={offerable}
                                canManage={canManage}
                                disabledReason={whyNotEditable(context, members, member)}
                                busy={busy === member.memberId}
                                onRoleChange={roleId => run(
                                    member.memberId,
                                    () => changeMemberRole(member.memberId, roleId),
                                    `${member.displayName || member.email} is now in a different role.`,
                                )}
                                onStatusChange={status => run(
                                    member.memberId,
                                    () => setMemberStatus(member.memberId, status),
                                    status === 'suspended'
                                        ? 'They can no longer sign in to this company.'
                                        : 'They can sign in to this company again.',
                                )}
                                onRemove={() => setConfirming(member)}
                            />
                        ))}
                    </ul>
                )}
            </Card>

            {canManage && (
                <>
                    <div className="mt-9">
                        <SectionHeader
                            title="Invitations"
                            subtitle="Sent to one address, good once, and they expire."
                        />
                    </div>

                    <Card className="mt-5 overflow-hidden">
                        {invites.length === 0 ? (
                            <EmptyState
                                title="No invitations outstanding"
                                hint="Invite somebody below and they will get an email with a link that works once."
                            />
                        ) : (
                            <ul className="divide-y divide-separator">
                                {invites.map(invite => (
                                    <InviteRow
                                        key={invite.id}
                                        invite={invite}
                                        busy={busy === invite.id}
                                        onResend={() => run(
                                            invite.id,
                                            async () => { await resendInvite(context.workspace.id, invite.id); },
                                            `Sent again to ${invite.email}. The earlier link no longer works.`,
                                        )}
                                        onRevoke={() => run(
                                            invite.id,
                                            () => revokeInvite(invite.id),
                                            `The invitation to ${invite.email} has been withdrawn.`,
                                        )}
                                    />
                                ))}
                            </ul>
                        )}
                    </Card>

                    <InviteForm
                        roles={offerable}
                        busy={busy === 'invite'}
                        onInvite={(email, roleId) => run(
                            'invite',
                            async () => { await inviteMember(context.workspace.id, email, roleId); },
                            `An invitation is on its way to ${email}.`,
                        )}
                    />
                </>
            )}

            <ConfirmDialog
                open={confirming !== null}
                title="Remove from this company?"
                confirmLabel="Remove"
                busy={busy === confirming?.memberId}
                onCancel={() => setConfirming(null)}
                onConfirm={() => {
                    const member = confirming;
                    setConfirming(null);
                    if (member) {
                        void run(
                            member.memberId,
                            () => removeMember(member.memberId),
                            `${member.displayName || member.email} is no longer in this company.`,
                        );
                    }
                }}
            >
                <p>
                    <strong>{confirming?.displayName || confirming?.email}</strong> will lose access to this
                    company immediately. Their follow-ups and history stay, and accounts assigned to them
                    become unassigned.
                </p>
                <p className="mt-2">They can be invited back later, as a new invitation.</p>
            </ConfirmDialog>
        </Page>
    );
}

/* -------------------------------------------------------------------------- */

const STATUS_TONE = { active: 'pos', suspended: 'warn', removed: 'neutral' } as const;

function MemberRow({
    member, offerable, canManage, disabledReason, busy,
    onRoleChange, onStatusChange, onRemove,
}: {
    member: TeamMember;
    offerable: { id: string; name: string }[];
    canManage: boolean;
    disabledReason: string | null;
    busy: boolean;
    onRoleChange: (roleId: string) => void;
    onStatusChange: (status: 'active' | 'suspended') => void;
    onRemove: () => void;
}) {
    // The role the member is actually in may be one this person cannot assign —
    // an owner seen by a delegate, say. It is still shown, so the list is
    // truthful; it just cannot be chosen.
    const options = offerable.some(r => r.id === member.roleId)
        ? offerable
        : [{ id: member.roleId, name: member.roleName }, ...offerable];

    const locked = !canManage || disabledReason !== null || busy;

    return (
        <li className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14.5px] font-semibold text-label truncate">
                        {member.displayName || member.email}
                    </span>
                    {member.isSelf && <Badge tone="brand">You</Badge>}
                    {member.isOwner && <Badge tone="brand">Owner</Badge>}
                    {member.status !== 'active' && (
                        <Badge tone={STATUS_TONE[member.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
                            {member.status === 'suspended' ? 'Suspended' : member.status}
                        </Badge>
                    )}
                </div>
                <div className="text-[13px] text-label-3 mt-0.5 truncate">{member.email}</div>
                {disabledReason && canManage && (
                    <div className="text-[12.5px] text-label-3 mt-1.5">{disabledReason}</div>
                )}
            </div>

            {/* max-w-full, not flex-none: on a narrow phone the controls must
                wrap inside the row rather than run out of the card. */}
            <div className="flex items-center gap-2 flex-wrap max-w-full">
                {canManage ? (
                    <label className="sr-only" htmlFor={`role-${member.memberId}`}>
                        Role for {member.displayName || member.email}
                    </label>
                ) : null}
                {canManage ? (
                    <select
                        id={`role-${member.memberId}`}
                        value={member.roleId}
                        disabled={locked}
                        onChange={event => onRoleChange(event.target.value)}
                        className={cx(
                            'h-9 px-3 rounded-[10px] bg-card border border-separator-strong',
                            'text-[13.5px] text-label outline-none focus:border-accent',
                            locked && 'opacity-40 cursor-not-allowed',
                        )}
                    >
                        {options.map(role => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                ) : (
                    <Badge>{member.roleName}</Badge>
                )}

                {canManage && (
                    <>
                        <Button
                            size="sm"
                            variant="quiet"
                            disabled={locked}
                            onClick={() => onStatusChange(member.status === 'active' ? 'suspended' : 'active')}
                        >
                            {member.status === 'active' ? 'Suspend' : 'Reinstate'}
                        </Button>
                        <Button size="sm" variant="quiet" disabled={locked} onClick={onRemove}>
                            Remove
                        </Button>
                    </>
                )}
            </div>
        </li>
    );
}

function InviteRow({
    invite, busy, onResend, onRevoke,
}: {
    invite: PendingInvite;
    busy: boolean;
    onResend: () => void;
    onRevoke: () => void;
}) {
    const expires = new Date(invite.expiresAt);
    return (
        <li className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
                <div className="text-[14.5px] font-semibold text-label truncate">{invite.email}</div>
                <div className="text-[13px] text-label-3 mt-0.5">
                    {invite.roleName}
                    {invite.invitedByName && <> · invited by {invite.invitedByName}</>}
                    {' · '}
                    <time dateTime={invite.expiresAt}>
                        expires {expires.toISOString().slice(0, 10)}
                    </time>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-none">
                <Button size="sm" variant="secondary" disabled={busy} onClick={onResend}>
                    Send again
                </Button>
                <Button size="sm" variant="quiet" disabled={busy} onClick={onRevoke}>
                    Withdraw
                </Button>
            </div>
        </li>
    );
}

function InviteForm({
    roles, busy, onInvite,
}: {
    roles: { id: string; name: string }[];
    busy: boolean;
    onInvite: (email: string, roleId: string) => void;
}) {
    const [email, setEmail] = useState('');
    const [roleId, setRoleId] = useState(roles[0]?.id ?? '');
    const [problem, setProblem] = useState<string | null>(null);

    const chosen = roles.some(r => r.id === roleId) ? roleId : roles[0]?.id ?? '';

    return (
        <Card className="mt-5 px-5 py-5">
            <h3 className="text-[15px] font-bold text-label tracking-[-0.02em]">Invite somebody</h3>
            <p className="text-[13px] text-label-3 mt-1 mb-4 leading-relaxed">
                They will get an email with a link that works once and only from that address.
                Forwarding it does not pass the invitation on.
            </p>

            <form
                className="flex items-end gap-3 flex-wrap"
                // The browser's own validation bubble is unstyled, worded
                // differently in every browser, and silently prevents submit —
                // which means the sentence this product would have shown is
                // never reached. `type="email"` stays, for the keyboard it
                // brings up on a phone; the judgement is ours.
                noValidate
                onSubmit={event => {
                    event.preventDefault();
                    const address = normaliseEmail(email);
                    if (!isEmailShaped(address)) {
                        setProblem('That does not look like an email address.');
                        return;
                    }
                    if (!chosen) {
                        setProblem('Choose a role for them.');
                        return;
                    }
                    setProblem(null);
                    setEmail('');
                    onInvite(address, chosen);
                }}
            >
                <div className="flex-1 min-w-[220px]">
                    <Field
                        label="Email address"
                        name="invite-email"
                        type="email"
                        autoComplete="off"
                        value={email}
                        error={problem}
                        onChange={event => setEmail(event.target.value)}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="invite-role" className="block text-[13px] font-semibold text-label mb-1.5">
                        Role
                    </label>
                    <select
                        id="invite-role"
                        value={chosen}
                        onChange={event => setRoleId(event.target.value)}
                        className="h-11 px-3.5 rounded-[11px] bg-card border border-separator-strong text-[15px] text-label outline-none focus:border-accent"
                    >
                        {roles.map(role => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                </div>
                <div className="mb-4">
                    <Button type="submit" variant="primary" disabled={busy || roles.length === 0}>
                        {busy ? 'Sending…' : 'Send invitation'}
                    </Button>
                </div>
            </form>

            {roles.length === 0 && (
                <p className="text-[13px] text-label-3">
                    There is no role you can invite somebody into. You can only offer a role whose
                    permissions you hold yourself.
                </p>
            )}
        </Card>
    );
}
